import { useEffect } from 'react';

import { useLocation } from 'react-router';
import { create } from 'zustand';

import { resolveCombinedBarHeight, SCROLL_DEADZONE } from '@/shared/configs/toolbar.config';

import { useIsDesktop } from '@/client/hooks/breakpoint.hooks';
import { breakpointStore } from '@/client/stores/breakpoint.store';

type ToolbarState = {
  /** Whether the fixed two-tier toolbar is translated out of view (mobile only). */
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
};

/**
 * Private module-level singleton holding the one `hidden` value that the driver
 * ({@link useScrollHideState}) writes and every reader ({@link useToolbarHidden})
 * subscribes to. It is deliberately **not exported**: the Toolbar and the
 * StickyContainer live in disjoint DOM subtrees (the toolbar's scroll-hide
 * `transform` can't contain the sticky sub-header), so a shared module
 * singleton — not a React tree — is what carries one decision to both and
 * survives client navigation. Exactly one listener feeds it; readers only read.
 */
const toolbarStore = create<ToolbarState>((set) => ({
  hidden: false,
  setHidden: (hidden) => set({ hidden }),
}));

/**
 * Pure scroll-hide decision for the mobile toolbar.
 *
 * Given the previous hidden state, the current scroll position and the move
 * since the anchor, decide whether the fixed two-tier toolbar should be
 * hidden. The bars only hide once the page has scrolled past the combined bar
 * height; sub-deadzone jitter keeps the previous state (the caller computes
 * `clearedDeadzone` once and shares it with the anchor advance); scrolling up
 * always reveals immediately.
 */
const resolveNextHidden = (
  prevHidden: boolean,
  currentY: number,
  delta: number,
  combinedBarHeight: number,
  clearedDeadzone: boolean,
): boolean => {
  // Within the combined bar height the bars are always visible.
  if (currentY <= combinedBarHeight) return false;

  // Ignore sub-deadzone jitter — keep whatever state we were in.
  if (!clearedDeadzone) return prevHidden;

  // Scrolling down hides; scrolling up reveals immediately.
  return delta > 0;
};

/**
 * Drives and reads the shared toolbar `hidden` state from the window scroll
 * position, mobile only: hide on scroll-down past the combined bar height,
 * reveal on scroll-up. Owns the single scroll listener and returns the current
 * `hidden` value for its caller (the Toolbar). Siblings that only mirror the
 * slide read {@link useToolbarHidden} instead of calling this — that keeps
 * exactly one listener regardless of how many components show the state.
 *
 * On desktop (`lg`+) the toolbar is forced visible and no scroll listener runs.
 */
export const useScrollHideState = (): boolean => {
  const isDesktop = useIsDesktop();
  // `hidden` is global and survives client navigation. Re-running this effect on
  // pathname change re-reveals the toolbar for the new page, so a page that hid
  // the bar then routed to a short/unscrollable page can't strand the top chrome
  // off-screen.
  const { pathname } = useLocation();

  useEffect(() => {
    const { setHidden } = toolbarStore.getState();

    if (isDesktop) {
      setHidden(false);
      return;
    }

    // Fresh listener (mount / route change) → start visible and anchor to the
    // current scroll position. The anchor is deliberately effect-local: it must
    // re-anchor whenever the listener is rebuilt.
    setHidden(false);
    let lastY = window.scrollY;
    let frame = 0;

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const currentY = window.scrollY;
        // Read the bar height from the store inside the listener rather than
        // subscribing to it: a sm/md breakpoint change updates the next scroll
        // calculation without re-rendering the whole Toolbar subtree.
        const { isSm, isMd } = breakpointStore.getState();
        const barHeight = resolveCombinedBarHeight(isSm, isMd);
        const { hidden } = toolbarStore.getState();
        // One deadzone check shared by the hide decision and the anchor
        // advance. Only advance once the move clears the deadzone, so a slow
        // continuous scroll accumulates instead of resetting every frame
        // (which would keep every per-frame delta under the deadzone and
        // never hide).
        const delta = currentY - lastY;
        const clearedDeadzone = Math.abs(delta) >= SCROLL_DEADZONE;
        setHidden(resolveNextHidden(hidden, currentY, delta, barHeight, clearedDeadzone));
        if (clearedDeadzone) lastY = currentY;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isDesktop, pathname]);

  return toolbarStore((s) => s.hidden);
};

/**
 * Read-only access to the shared toolbar `hidden` value, for siblings that
 * mirror the Toolbar's slide (the mobile StickyContainer) without starting
 * their own scroll listener. The listener is owned solely by
 * {@link useScrollHideState}.
 */
export const useToolbarHidden = (): boolean => toolbarStore((s) => s.hidden);
